import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { JobCard, JobViewModal, JobApplyModal } from '../components/jobs/JobComponents';
import { Truck, Search, Plus, X, Edit3 } from 'lucide-react';
import { toast } from 'sonner';

const EQUIPMENT_TYPES = ['Box Truck - 10ft', 'Box Truck - 12ft', 'Box Truck - 14ft', 'Box Truck - 16ft', 'Box Truck - 20ft', 'Box Truck - 24ft', 'Box Truck - 26ft', 'Cargo Van', 'Sprinter Van', 'Flatbed', 'Dry Van', 'Hotshot'];
const ROUTE_TYPES = ['Local', 'OTR', 'Regional'];
const CDL_OPTIONS = ['Non-CDL', 'CDL - Class A', 'CDL - Class B', 'CDL - Class C'];
const FREIGHT_TYPES = ['General Freight', 'Household Goods', 'E-Commerce / Last Mile', 'Medical / Pharmaceutical', 'Food & Beverage', 'Electronics', 'Construction Materials', 'Hazmat', 'White Glove', 'Other'];
const SCHEDULE_OPTIONS = ['Full-Time', 'Part-Time', 'Contract', 'Per Diem', 'On-Call', 'Seasonal'];
const EXPERIENCE_OPTIONS = ['No Experience Needed', '6 months+', '1 year+', '2 years+', '3 years+', '5 years+'];
const REQUIREMENTS_OPTIONS = ['None', 'TWIC Card', 'TSA PreCheck', 'Hazmat Endorsement', 'DOT Physical', 'Clean Driving Record', 'Background Check', 'Drug Test', 'Liftgate Experience', 'Pallet Jack Certified'];

const EMPTY_FORM = {
  title: '', company: '', location: '', cdl_type: 'Non-CDL', pay: '', pay_type: 'flat',
  schedule: 'Full-Time', freight_type: 'General Freight', equipment_type: 'Box Truck - 26ft',
  experience_required: 'No Experience Needed', contact_info: '', description: '',
  start_date: '', route_type: 'Local', equipment_needed: '', requirements: '',
  mc_number: '', dot_number: '',
};

const Jobs = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [viewingJob, setViewingJob] = useState(null);
  const [applyingTo, setApplyingTo] = useState(null);
  const [applyMessage, setApplyMessage] = useState('');
  const [submittingApp, setSubmittingApp] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [selectedReqs, setSelectedReqs] = useState([]);

  const isCarrier = user?.role === 'carrier_owner' || user?.is_admin;
  const isDispatcher = user?.role === 'dispatcher';
  const isDriver = user?.role === 'driver';

  const fetchJobs = useCallback(async () => {
    try {
      const response = await api.get('/jobs/all');
      setJobs(response.data.jobs || []);
    } catch { toast.error('Failed to load jobs'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const resetForm = () => { setForm({ ...EMPTY_FORM }); setSelectedReqs([]); };

  const handleCreateJob = async () => {
    if (!form.title || !form.location) { toast.error('Job title and location are required'); return; }
    const payload = { ...form, requirements: selectedReqs.join(', ') };
    try {
      await api.post('/jobs/create', payload);
      toast.success('Job posted successfully!');
      setShowCreateModal(false); resetForm(); fetchJobs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create job'); }
  };

  const handleUpdateJob = async () => {
    const payload = { ...form, requirements: selectedReqs.join(', ') };
    try {
      await api.put(`/jobs/${editingJob.id}`, payload);
      toast.success('Job updated!');
      setEditingJob(null); resetForm(); fetchJobs();
    } catch { toast.error('Failed to update job'); }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Delete this job posting?')) return;
    try { await api.delete(`/jobs/${jobId}`); toast.success('Job deleted'); fetchJobs(); }
    catch { toast.error('Failed to delete job'); }
  };

  const handleApplyJob = async (jobId) => {
    setSubmittingApp(true);
    try {
      await api.post(`/jobs/${jobId}/apply`, { message: applyMessage });
      toast.success('Application submitted!');
      setApplyingTo(null); setApplyMessage(''); fetchJobs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to apply'); }
    finally { setSubmittingApp(false); }
  };

  const toggleReq = (req) => {
    setSelectedReqs(prev => prev.includes(req) ? prev.filter(r => r !== req) : [...prev, req]);
  };

  const openEdit = (j) => {
    setForm({ ...EMPTY_FORM, ...j });
    setSelectedReqs(j.requirements ? j.requirements.split(', ').filter(Boolean) : []);
    setEditingJob(j);
  };

  const filteredJobs = jobs.filter(j => {
    const matchSearch = j.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      j.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      j.company?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchFilter = filterType === 'all' || j.status === filterType || j.route_type === filterType;
    return matchSearch && matchFilter;
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="jobs-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Truck className="h-6 w-6 text-blue-600" />
              {isCarrier ? 'Job Board — Post & Manage' : isDispatcher ? 'Job Board — Track & Manage' : 'Available Jobs'}
            </h1>
            <p className="text-slate-500 text-sm mt-1">{filteredJobs.length} jobs available</p>
          </div>
          {isCarrier && (
            <button onClick={() => { resetForm(); setShowCreateModal(true); }}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg font-medium transition-colors" data-testid="post-job-btn">
              <Plus className="h-4 w-4" />Post New Job
            </button>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input type="text" placeholder="Search by title, location, or company..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="jobs-search" />
          </div>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
            className="border border-slate-200 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="jobs-filter">
            <option value="all">All Jobs</option>
            <option value="open">Open</option>
            <option value="Local">Local</option>
            <option value="OTR">OTR</option>
            <option value="Regional">Regional</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-400">Loading jobs...</div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-20"><Truck className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No jobs found</p></div>
        ) : (
          <div className="grid gap-4">
            {filteredJobs.map((job) => (
              <JobCard key={job.id} job={job} isDriver={isDriver} isCarrier={isCarrier} isDispatcher={isDispatcher}
                onView={setViewingJob} onApply={setApplyingTo}
                onEdit={openEdit} onDelete={handleDeleteJob} />
            ))}
          </div>
        )}
      </main>
      <Footer />

      <AnimatePresence>{viewingJob && <JobViewModal job={viewingJob} onClose={() => setViewingJob(null)} />}</AnimatePresence>

      {/* Create / Edit Job Modal */}
      <AnimatePresence>
        {(showCreateModal || editingJob) && (
          <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-8">
            <div className="bg-white rounded-2xl w-full max-w-2xl mx-4 p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-slate-900">{editingJob ? 'Edit Job' : 'Post New Job'}</h3>
                <button onClick={() => { setShowCreateModal(false); setEditingJob(null); resetForm(); }} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Company Name */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Company Name *</label>
                  <input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Your company name" data-testid="job-company-input" />
                </div>
                {/* Job Title */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Job Title *</label>
                  <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. Box Truck Driver Needed" data-testid="job-title-input" />
                </div>
                {/* Location */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Location *</label>
                  <input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="City, State" data-testid="job-location-input" />
                </div>
                {/* CDL Type */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">CDL Requirement</label>
                  <select value={form.cdl_type} onChange={(e) => setForm({...form, cdl_type: e.target.value})}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="job-cdl-select">
                    {CDL_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                {/* Pay */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Pay Rate ($)</label>
                  <input value={form.pay} onChange={(e) => setForm({...form, pay: e.target.value})}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. 2500" data-testid="job-pay-input" />
                </div>
                {/* Pay Type */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Pay Type</label>
                  <select value={form.pay_type} onChange={(e) => setForm({...form, pay_type: e.target.value})}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="job-paytype-select">
                    <option value="flat">Flat Rate</option><option value="per_mile">Per Mile</option><option value="hourly">Hourly</option><option value="weekly">Weekly</option><option value="percentage">Percentage</option>
                  </select>
                </div>
                {/* Schedule */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Schedule</label>
                  <select value={form.schedule} onChange={(e) => setForm({...form, schedule: e.target.value})}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="job-schedule-select">
                    {SCHEDULE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                {/* Route Type */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Route Type</label>
                  <select value={form.route_type} onChange={(e) => setForm({...form, route_type: e.target.value})}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="job-route-select">
                    {ROUTE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                {/* Type of Freight */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Type of Freight</label>
                  <select value={form.freight_type} onChange={(e) => setForm({...form, freight_type: e.target.value})}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="job-freight-select">
                    {FREIGHT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                {/* Equipment Type */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Vehicle / Equipment Needed</label>
                  <select value={form.equipment_type} onChange={(e) => setForm({...form, equipment_type: e.target.value})}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="job-equipment-select">
                    {EQUIPMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                {/* Experience Required */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Experience Required</label>
                  <select value={form.experience_required} onChange={(e) => setForm({...form, experience_required: e.target.value})}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="job-experience-select">
                    {EXPERIENCE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                {/* Start Date */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Start Date</label>
                  <input type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="job-startdate-input" />
                </div>
                {/* Contact Information */}
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Contact Information</label>
                  <input value={form.contact_info} onChange={(e) => setForm({...form, contact_info: e.target.value})}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Email, phone, or preferred contact method" data-testid="job-contact-input" />
                </div>
                {/* MC / DOT (Optional) */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">MC Number <span className="text-slate-400 font-normal">(optional)</span></label>
                  <input value={form.mc_number} onChange={(e) => setForm({...form, mc_number: e.target.value})}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="MC-123456" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">DOT Number <span className="text-slate-400 font-normal">(optional)</span></label>
                  <input value={form.dot_number} onChange={(e) => setForm({...form, dot_number: e.target.value})}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="DOT-789012" />
                </div>
                {/* Requirements (chips) */}
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Requirements (optional)</label>
                  <div className="flex flex-wrap gap-2">
                    {REQUIREMENTS_OPTIONS.map(req => (
                      <button key={req} type="button" onClick={() => toggleReq(req)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${selectedReqs.includes(req) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}
                        data-testid={`req-${req.replace(/\s/g, '-')}`}>
                        {req}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Job Description */}
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Job Description</label>
                  <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={4}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe the job, duties, expectations, and any additional details..." data-testid="job-description-input" />
                </div>
                {/* Submit */}
                <div className="sm:col-span-2">
                  <button onClick={editingJob ? handleUpdateJob : handleCreateJob}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2" data-testid="submit-job-btn">
                    {editingJob ? <><Edit3 className="h-4 w-4" />Update Job</> : <><Plus className="h-4 w-4" />Post Job</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {applyingTo && <JobApplyModal job={applyingTo} message={applyMessage} setMessage={setApplyMessage}
          onSubmit={() => handleApplyJob(applyingTo.id)} submitting={submittingApp} onClose={() => setApplyingTo(null)} />}
      </AnimatePresence>
    </div>
  );
};

export default Jobs;
